import { formatBytes } from './cloudinaryFormatters';

export interface CloudinaryResource {
  publicId: string;
  format: string;
  resourceType: string;
  bytes: number;
  width?: number;
  height?: number;
  url: string;
  hasDbRecord: boolean;
  dbRecord?: {
    isPublished: boolean;
    status: string;
  };
}

export interface CloudinaryResourceListProps {
  resources: CloudinaryResource[];
  isLoading: boolean;
  selectedResource: string | null;
  onSelectResource: (publicId: string) => void;
}

export const CloudinaryResourceList = ({
  resources,
  isLoading,
  selectedResource,
  onSelectResource,
}: CloudinaryResourceListProps) => (
  <div className="bg-gray-50 rounded-lg shadow">
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">Resources ({resources.length})</h3>
    </div>

    <div className="h-[600px] overflow-y-auto">
      {isLoading ? (
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading resources...</p>
        </div>
      ) : resources.length === 0 ? (
        <div className="p-6 text-center text-gray-500">No resources found</div>
      ) : (
        <div className="divide-y divide-gray-200">
          {resources.map((resource) => (
            <div
              key={resource.publicId}
              onClick={() => onSelectResource(resource.publicId)}
              className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedResource === resource.publicId
                  ? 'bg-primary-50 border-r-4 border-primary-500'
                  : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  {resource.resourceType === 'image' ? (
                    <img
                      src={resource.url}
                      alt={resource.publicId}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : resource.resourceType === 'video' ? (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      🎥
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      📄
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{resource.publicId}</p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-gray-500">{formatBytes(resource.bytes)}</span>
                    <span className="text-xs text-gray-500">{resource.format.toUpperCase()}</span>
                    {resource.width && resource.height && (
                      <span className="text-xs text-gray-500">
                        {resource.width}×{resource.height}
                      </span>
                    )}
                  </div>

                  {/* Status indicators */}
                  <div className="flex items-center space-x-2 mt-2">
                    {resource.hasDbRecord ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ In Database
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        ⚠️ Orphaned
                      </span>
                    )}

                    {resource.dbRecord && (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          resource.dbRecord.isPublished
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {resource.dbRecord.isPublished ? 'Published' : 'Unpublished'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
